import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useState, useMemo } from "react";
import Header from "@/components/Header";
import TrailMap from "@/components/TrailMap";
import TrailsList from "@/components/TrailsList";
import RelatedItemsPanel from "@/components/RelatedItemsPanel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Filter, Search, Upload, Trophy, Users, TrendingUp, Map, Route, Activity, MapPin, Heart, Target } from "lucide-react";
import { useTrails, useTrailSegments } from "@/hooks/useTrails";
import { useRoutes } from "@/hooks/useRoutes";
import { useRuns } from "@/hooks/useRuns";
import { useUserTrailPreferences } from "@/hooks/useTrailPreferences";

const TrailExplorer = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");
  const [filterTab, setFilterTab] = useState("all");
  const [isMobileSheetOpen, setIsMobileSheetOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [mapInstance, setMapInstance] = useState<any>(null);
  const [selectedItem, setSelectedItem] = useState<{
    id: string;
    type: 'segment' | 'route' | 'run';
    name: string;
  } | null>(null);

  // Simplified data hooks - no filters applied to segments
  const trailFilters = useMemo(() => ({
    search: searchQuery
  }), [searchQuery]);

  const { trails, isLoading: trailsLoading } = useTrails(trailFilters);
  const { segments, isLoading: segmentsLoading } = useTrailSegments();
  const { routes, isLoading: routesLoading } = useRoutes();
  const { runs, isLoading: runsLoading } = useRuns();
  const { favorites, wantToGo, isLoading: preferencesLoading } = useUserTrailPreferences();

  // Get trail IDs for filtering
  const favoriteTrailIds = useMemo(() => new Set(favorites.map(f => f.trail_id)), [favorites]);
  const wantToGoTrailIds = useMemo(() => new Set(wantToGo.map(w => w.trail_id)), [wantToGo]);

  // Handle zoom to location
  const handleZoomToLocation = (coordinates: [number, number], zoomLevel: number = 14) => {
    if (mapInstance) {
      mapInstance.flyTo({
        center: coordinates,
        zoom: zoomLevel,
        duration: 2000
      });
    }
  };

  // Handle item selection for showing related data
  const handleItemSelect = (id: string, type: 'segment' | 'route' | 'run', name: string) => {
    setSelectedItem({ id, type, name });
  };

  if (isLoading) {
    return <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Loading...</p>
      </div>
    </div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Trail Explorer</h1>
              <p className="text-muted-foreground">
                Discover and explore 4x4 trails across Southern California
              </p>
            </div>
            
            {user && (
              <Button 
                className="flex items-center gap-2"
                onClick={() => navigate('/log-run')}
              >
                <Upload className="h-4 w-4" />
                Log a Run
              </Button>
            )}
          </div>

          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder="Search trails, routes, runs..." 
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            {user && (
              <div className="flex gap-2">
                <Button
                  variant={filterTab === "favorites" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilterTab(filterTab === "favorites" ? "all" : "favorites")}
                  className="gap-1"
                >
                  <Heart className={`h-4 w-4 ${filterTab === "favorites" ? "fill-current" : ""}`} />
                  Favorites
                  {favorites.length > 0 && (
                    <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                      {favorites.length}
                    </Badge>
                  )}
                </Button>
                <Button
                  variant={filterTab === "wantToGo" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilterTab(filterTab === "wantToGo" ? "all" : "wantToGo")}
                  className="gap-1"
                >
                  <Target className={`h-4 w-4 ${filterTab === "wantToGo" ? "fill-current" : ""}`} />
                  Want to Go
                  {wantToGo.length > 0 && (
                    <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                      {wantToGo.length}
                    </Badge>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:grid lg:grid-cols-6 gap-6">
          {/* Map Section */}
          <div className="lg:col-span-4">
            <Card className="overflow-hidden">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <div className="p-4 border-b">
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="all" className="flex items-center gap-2">
                      <Search className="h-4 w-4" />
                      <span className="hidden sm:inline">All</span>
                    </TabsTrigger>
                    <TabsTrigger value="trails" className="flex items-center gap-2">
                      <Map className="h-4 w-4" />
                      <span className="hidden sm:inline">Trails</span>
                    </TabsTrigger>
                    <TabsTrigger value="activity" className="flex items-center gap-2">
                      <Activity className="h-4 w-4" />
                      <span className="hidden sm:inline">Activity</span>
                    </TabsTrigger>
                    <TabsTrigger value="features" className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      <span className="hidden sm:inline">Features</span>
                    </TabsTrigger>
                  </TabsList>
                </div>
                
                <TabsContent value="all" className="p-0">
                  <TrailMap className="h-[600px]" segments={segments} />
                </TabsContent>
                <TabsContent value="trails" className="p-0">
                  <TrailMap className="h-[600px]" segments={segments} />
                </TabsContent>
                <TabsContent value="activity" className="p-0">
                  <TrailMap className="h-[600px]" segments={segments} />
                </TabsContent>
                <TabsContent value="features" className="p-0">
                  <TrailMap className="h-[600px]" segments={segments} />
                </TabsContent>
              </Tabs>
            </Card>
          </div>

          {/* Desktop Sidebar - Lists and Related Data */}
          <div className="lg:col-span-2 space-y-6">
            {/* Main List */}
            <div className="h-[400px]">
              <TrailsList 
                segments={segments.filter(segment => {
                  const matchesSearch = !searchQuery || 
                    segment.trail_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    segment.forest_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    segment.district_name?.toLowerCase().includes(searchQuery.toLowerCase());
                  
                  if (filterTab === "favorites") {
                    return matchesSearch && favoriteTrailIds.has(segment.trail_id);
                  }
                  if (filterTab === "wantToGo") {
                    return matchesSearch && wantToGoTrailIds.has(segment.trail_id);
                  }
                  return matchesSearch;
                })}
                routes={routes.filter(route =>
                  !searchQuery ||
                  route.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  route.description?.toLowerCase().includes(searchQuery.toLowerCase())
                )}
                runs={runs.filter(run =>
                  !searchQuery ||
                  run.equipment_type?.toLowerCase().includes(searchQuery.toLowerCase())
                )}
                activeTab={activeTab}
                isLoading={segmentsLoading || routesLoading || runsLoading}
                onZoomToLocation={handleZoomToLocation}
                onItemSelect={handleItemSelect}
              />
            </div>

            {/* Related Items Panel */}
            <div className="h-[400px]">
              <RelatedItemsPanel 
                itemId={selectedItem?.id}
                itemType={selectedItem?.type}
                itemName={selectedItem?.name}
                onZoomToLocation={handleZoomToLocation}
              />
            </div>

            {/* Call to Action */}
            {!user && (
              <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                <CardHeader>
                  <CardTitle className="text-lg">Join the Community</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    Sign up free to upload your GPX tracks, earn badges, and unlock detailed trail information.
                  </p>
                  <Button 
                    className="w-full" 
                    onClick={() => navigate('/signup')}
                  >
                    Get Started Free
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="lg:hidden">
          <div className="grid grid-cols-1 gap-4">
            {/* Mobile Map with Tabs */}
            <Card className="overflow-hidden">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <div className="p-4 border-b">
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="all" className="flex items-center gap-1">
                      <Search className="h-4 w-4" />
                      <span className="text-xs">All</span>
                    </TabsTrigger>
                    <TabsTrigger value="trails" className="flex items-center gap-1">
                      <Map className="h-4 w-4" />
                      <span className="text-xs">Trails</span>
                    </TabsTrigger>
                    <TabsTrigger value="activity" className="flex items-center gap-1">
                      <Activity className="h-4 w-4" />
                      <span className="text-xs">Activity</span>
                    </TabsTrigger>
                    <TabsTrigger value="features" className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      <span className="text-xs">POIs</span>
                    </TabsTrigger>
                  </TabsList>
                </div>
                
                <TabsContent value="all" className="p-0">
                  <TrailMap className="h-[400px]" segments={segments} />
                </TabsContent>
                <TabsContent value="trails" className="p-0">
                  <TrailMap className="h-[400px]" segments={segments} />
                </TabsContent>
                <TabsContent value="activity" className="p-0">
                  <TrailMap className="h-[400px]" segments={segments} />
                </TabsContent>
                <TabsContent value="features" className="p-0">
                  <TrailMap className="h-[400px]" segments={segments} />
                </TabsContent>
              </Tabs>
            </Card>

            {/* Mobile Bottom Sheet - Real Data */}
            <Sheet open={isMobileSheetOpen} onOpenChange={setIsMobileSheetOpen}>
              <SheetTrigger asChild>
                  <Button variant="outline" className="w-full">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  {activeTab === 'all' ? `View All (${segments.length + routes.length + runs.length})` :
                   activeTab === 'trails' ? `View ${segments.length} Trail Segments` :
                   activeTab === 'activity' ? `View ${routes.length + runs.length} Activities` :
                   'View Features & POIs'}
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="h-[80vh]">
                <SheetHeader>
                  <SheetTitle>
                    {activeTab === 'all' ? 'All Results' :
                     activeTab === 'trails' ? 'Trail Segments' :
                     activeTab === 'activity' ? 'Community Activity' :
                     'Features & Points of Interest'}
                  </SheetTitle>
                  <SheetDescription>
                    {activeTab === 'all' ? 'Search across trails, routes, runs, and features' :
                     activeTab === 'trails' ? 'Explore available trail segments' :
                     activeTab === 'activity' ? 'View user-created routes and recent runs' :
                     'Discover points of interest, obstacles, and campsites'}
                  </SheetDescription>
                </SheetHeader>
                
                <div className="mt-6 overflow-y-auto h-full pb-20">
                  <TrailsList 
                    segments={segments.filter(segment => 
                      !searchQuery || 
                      segment.trail_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      segment.forest_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      segment.district_name?.toLowerCase().includes(searchQuery.toLowerCase())
                    )}
                    routes={routes.filter(route =>
                      !searchQuery ||
                      route.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      route.description?.toLowerCase().includes(searchQuery.toLowerCase())
                    )}
                    runs={runs.filter(run =>
                      !searchQuery ||
                      run.equipment_type?.toLowerCase().includes(searchQuery.toLowerCase())
                    )}
                    activeTab={activeTab}
                    isLoading={segmentsLoading || routesLoading || runsLoading}
                    onZoomToLocation={handleZoomToLocation}
                    onItemSelect={handleItemSelect}
                  />

                  {selectedItem && (
                    <div className="mt-6">
                      <RelatedItemsPanel 
                        itemId={selectedItem.id}
                        itemType={selectedItem.type}
                        itemName={selectedItem.name}
                        onZoomToLocation={handleZoomToLocation}
                      />
                    </div>
                  )}

                  {!user && (
                    <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20 mt-6">
                      <CardHeader>
                        <CardTitle className="text-lg">Join the Community</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-4">
                          Sign up free to upload your GPX tracks, earn badges, and unlock detailed trail information.
                        </p>
                        <Button 
                          className="w-full" 
                          onClick={() => {
                            setIsMobileSheetOpen(false);
                            navigate('/signup');
                          }}
                        >
                          Get Started Free
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TrailExplorer;